import { watch } from 'vue'
import type { Router } from 'vue-router'
import { API_BASE_URL } from './config'
import guideCatalog from '@motexa/user-guide/catalog.json'
import { i18n } from './i18n'
import sdkNavigation from '../../../public-sdk/docs/navigation.json'

const VISITOR_KEY = 'motexa-website-visitor'
const ID_PATTERN = /^[A-Za-z0-9_-]{16,64}$/
const trackedPages = new Set(['/', '/download', '/docs', '/docs/developers', ...guideCatalog.articles.map(doc => `/docs/${doc.slug}`), ...sdkNavigation.articles.map(article => `/docs/developers/${article.slug}`)])
const enabled = import.meta.env.VITE_WEBSITE_ANALYTICS_ENABLED === 'true' ||
  (import.meta.env.PROD && import.meta.env.VITE_WEBSITE_ANALYTICS_ENABLED !== 'false')

// Keep identity in memory as a fallback when browser storage is unavailable.
function visitorID(): string {
  const generated = crypto.randomUUID()
  try {
    const stored = localStorage.getItem(VISITOR_KEY)
    if (stored && ID_PATTERN.test(stored)) return stored
    localStorage.setItem(VISITOR_KEY, generated)
  } catch { /* Anonymous visits still work in storage-restricted browsers. */ }
  return generated
}

export function installWebsiteAnalytics(router: Router): void {
  if (!enabled) return
  let visitor: string
  try { visitor = visitorID() } catch { return }
  const send = (kind: string, details: Record<string, unknown> = {}) => {
    const routePage = router.currentRoute.value.path
    if (!trackedPages.has(routePage)) return
    // Group SDK articles under the existing production analytics page so a
    // static website release does not require replacing the API service.
    const page = routePage.startsWith('/docs/developers/') ? '/docs/developers' : routePage
    try {
      void fetch(`${API_BASE_URL.replace(/\/$/, '')}/website/events`, {
        method: 'POST', credentials: 'omit', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId: crypto.randomUUID(), visitorId: visitor, kind, page, locale: i18n.global.locale.value, ...details }),
      }).catch(() => {})
    } catch { /* Navigation and downloads must not depend on analytics. */ }
  }
  const view = () => {
    const page = router.currentRoute.value.path
    if (page === '/') send('home_view')
    else if (page === '/docs' || page.startsWith('/docs/')) send('docs_view')
    else if (page === '/download') send('language_use')
  }
  view()
  window.addEventListener('pageshow', event => { if (event.persisted) view() })
  router.afterEach((to, from, failure) => {
    if (!failure && to.path !== from.path) view()
  })
  // Switching language measures usage without creating another page view.
  watch(i18n.global.locale, () => send('language_use'), { flush: 'sync' })
  const click = (event: MouseEvent) => {
    if (event.type === 'auxclick' && event.button !== 1) return
    const link = (event.target instanceof Element ? event.target : null)?.closest<HTMLAnchorElement>('a[data-site-event]')
    if (!link) return
    const kind = link.dataset.siteEvent
    const placement = link.dataset.sitePlacement
    if ((kind === 'download_entry' || kind === 'docs_entry') && router.currentRoute.value.path === '/') {
      send(kind, { placement })
    } else if (kind === 'apk_click' && router.currentRoute.value.path === '/download') {
      const versionCode = Number(link.dataset.siteVersion)
      if (Number.isInteger(versionCode) && versionCode > 0) {
        send(kind, { placement: 'apk', channel: link.dataset.siteChannel, versionCode, sourceHost: new URL(link.href).hostname })
      }
    }
  }
  document.addEventListener('click', click, { capture: true })
  document.addEventListener('auxclick', click, { capture: true })
}
